/**
 * Manager 基类
 * 提供所有 Manager 的通用功能
 */
import { ManagerRegistry } from './manager-registry';
import type { EngineEvents } from '../types/events';
import type { Engine } from '../components/engine';

/**
 * Manager 抽象基类
 * - 通过构造函数接收 Registry 实例
 * - 自动管理事件订阅的清理
 */
export abstract class BaseManager {
    /** 注册表实例 */
    protected registry: ManagerRegistry;
    /** 事件订阅列表，用于自动清理 */
    private subscriptions: Array<() => void> = [];

    constructor(registry: ManagerRegistry) {
        this.registry = registry;
    }

    /**
     * 获取 Engine 组件实例的便捷访问器
     * 内部管理器通过此 getter 直接访问 Engine 组件的全部能力
     * 替代原来的 this.registry.engine3d?.xxx() 透传链
     */
    protected get engineComponent(): Engine | null {
        return this.registry.engine3d?.getEngineComponent?.() ?? null;
    }

    /**
     * 订阅事件（自动追踪，destroy 时自动取消）
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    protected subscribe<K extends keyof EngineEvents>(
        event: K,
        handler: (payload: EngineEvents[K]) => void
    ): void {
        const unsubscribe = this.registry.on(event, handler);
        this.subscriptions.push(unsubscribe);
    }

    /**
     * 发送事件
     * @param event 事件名称
     * @param payload 事件数据
     */
    protected emit<K extends keyof EngineEvents>(
        event: K,
        payload: EngineEvents[K]
    ): void {
        this.registry.emit(event, payload);
    }

    /**
     * 销毁 Manager，清理所有订阅
     * 子类应该调用 super.destroy()
     */
    public destroy(): void {
        this.subscriptions.forEach(unsub => unsub());
        this.subscriptions = [];
    }
}
